/** @format */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  X,
  Calendar,
  Car,
  User,
  Clock,
  Image as ImageIcon,
  CheckCircle,
  Eye,
  ChevronDown,
  ChevronUp,
  Loader as LoaderIcon,
} from "lucide-react";
import { supabase } from "@lib/supabaseClient";

/**
 * Composant pour rechercher et sélectionner une réception existante
 * 
 * @param {function} onReceptionSelected - Callback appelé quand une réception est sélectionnée
 * @param {function} onClose - Callback pour fermer le composant
 */
const ReceptionSearch = ({ onReceptionSelected, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [receptions, setReceptions] = useState([]);
  const [filteredReceptions, setFilteredReceptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReception, setSelectedReception] = useState(null);
  const [expandedReception, setExpandedReception] = useState(null);
  const [photosUrls, setPhotosUrls] = useState({});
  const [error, setError] = useState(null);

  // Charger les réceptions récentes au montage
  useEffect(() => {
    console.log("🔍 [ReceptionSearch] Montage du composant");
    loadRecentReceptions();
  }, []);

  // Filtrer les réceptions selon la recherche
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredReceptions(receptions);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = receptions.filter(
        (r) =>
          r.immatriculation?.toLowerCase().includes(term) ||
          r.client_name?.toLowerCase().includes(term)
      );
      setFilteredReceptions(filtered);
      console.log(`🔍 Filtrage: "${term}" → ${filtered.length} résultat(s)`);
    }
  }, [searchTerm, receptions]);

  // Charger les 50 réceptions les plus récentes
  const loadRecentReceptions = async () => {
    console.log("📥 [ReceptionSearch] Chargement des réceptions...");
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await supabase
        .from("receptions")
        .select("*")
        .order("date_reception", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(50);

      if (fetchError) {
        console.error("❌ Erreur Supabase:", fetchError);
        throw fetchError;
      }

      console.log("✅ Réceptions chargées:", data?.length || 0);
      
      if (data && data.length > 0) {
        console.log("📋 Première réception:", data[0]);
        console.log("   - ID:", data[0].id);
        console.log("   - Immat:", data[0].immatriculation);
        console.log("   - Client:", data[0].client_name);
        console.log("   - Date:", data[0].date_reception);
        console.log("   - Photos:", data[0].photos ? Object.keys(data[0].photos) : "Aucune");
      } else {
        console.log("⚠️ Aucune réception trouvée dans la table");
      }
      
      setReceptions(data || []);
      setFilteredReceptions(data || []);
    } catch (err) {
      console.error("❌ Erreur chargement réceptions:", err);
      setError("Erreur lors du chargement des réceptions. Vérifiez la console.");
    } finally {
      setLoading(false);
    }
  };

  // Rechercher par immatriculation
  const searchByImmatriculation = async () => {
    if (!searchTerm.trim()) {
      loadRecentReceptions();
      return;
    }

    console.log(`🔍 Recherche serveur: "${searchTerm}"`);
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: searchError } = await supabase
        .from("receptions")
        .select("*")
        .or(`immatriculation.ilike.%${searchTerm}%,client_name.ilike.%${searchTerm}%`)
        .order("date_reception", { ascending: false })
        .limit(100);

      if (searchError) {
        console.error("❌ Erreur recherche:", searchError);
        throw searchError;
      }

      console.log("✅ Résultats recherche:", data?.length || 0);
      setReceptions(data || []);
      setFilteredReceptions(data || []);
    } catch (err) {
      console.error("❌ Erreur recherche:", err);
      setError("Erreur lors de la recherche. Vérifiez la console.");
    } finally {
      setLoading(false);
    }
  };

  // Charger les URLs signées des photos
  const loadPhotoUrls = async (receptionId, photos) => {
    if (!photos || typeof photos !== "object" || Object.keys(photos).length === 0) {
      console.log(`📸 Pas de photos pour réception ${receptionId}`);
      return;
    }

    console.log(`📸 Chargement URLs signées pour réception ${receptionId}...`);
    console.log("   Photos:", photos);

    try {
      const urls = {};
      let loadedCount = 0;
      
      for (const [key, fileName] of Object.entries(photos)) {
        if (fileName && typeof fileName === "string") {
          console.log(`   → Chargement ${key}: ${fileName}`);
          
          const { data, error } = await supabase.storage
            .from("vehicle-photos")
            .createSignedUrl(fileName, 3600); // 1h

          if (error) {
            console.error(`   ❌ Erreur ${key}:`, error);
          } else if (data?.signedUrl) {
            urls[key] = data.signedUrl;
            loadedCount++;
            console.log(`   ✅ ${key} chargé`);
          }
        }
      }

      console.log(`✅ ${loadedCount}/${Object.keys(photos).length} photos chargées`);
      
      setPhotosUrls((prev) => ({
        ...prev,
        [receptionId]: urls,
      }));
    } catch (err) {
      console.error("❌ Erreur chargement photos:", err);
    }
  };

  // Toggle expansion réception
  const toggleExpand = (receptionId) => {
    console.log(`👆 Toggle réception ${receptionId}`);
    
    if (expandedReception === receptionId) {
      setExpandedReception(null);
    } else {
      setExpandedReception(receptionId);
      const reception = receptions.find((r) => r.id === receptionId);
      if (reception?.photos && !photosUrls[receptionId]) {
        loadPhotoUrls(receptionId, reception.photos);
      }
    }
  };

  // Sélectionner une réception
  const handleSelect = (reception) => {
    console.log("✅ Réception sélectionnée:", reception);
    setSelectedReception(reception);
  };

  // Confirmer la sélection
  const confirmSelection = async () => {
    if (!selectedReception) {
      console.log("⚠️ Aucune réception sélectionnée");
      return;
    }

    console.log("📦 Confirmation sélection:", selectedReception);

    // Charger les URLs signées des photos
    const receptionWithUrls = { ...selectedReception };

    if (selectedReception.photos && typeof selectedReception.photos === "object") {
      console.log("📸 Chargement URLs signées pour confirmation...");
      
      try {
        const urls = {};
        for (const [key, fileName] of Object.entries(selectedReception.photos)) {
          if (fileName && typeof fileName === "string") {
            const { data, error } = await supabase.storage
              .from("vehicle-photos")
              .createSignedUrl(fileName, 3600);

            if (!error && data?.signedUrl) {
              urls[key] = data.signedUrl;
            }
          }
        }
        
        receptionWithUrls.photos = urls;
        console.log("✅ URLs signées chargées:", Object.keys(urls));
      } catch (err) {
        console.error("❌ Erreur chargement photos:", err);
      }
    }

    console.log("✅ Réception finale avec URLs:", receptionWithUrls);
    onReceptionSelected(receptionWithUrls);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-slate-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        
        {/* En-tête */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-purple-400 flex items-center gap-2">
              <Search className="w-6 h-6" />
              Rechercher une réception existante
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Barre de recherche */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && searchByImmatriculation()}
                placeholder="Rechercher par immatriculation ou client..."
                className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
              />
            </div>
            <button
              onClick={searchByImmatriculation}
              disabled={loading}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg transition-colors flex items-center gap-2">
              {loading ? (
                <>
                  <LoaderIcon className="animate-spin w-5 h-5" />
                  Recherche...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  Rechercher
                </>
              )}
            </button>
          </div>

          {/* Compteur et erreur */}
          <div className="mt-2 flex items-center justify-between">
            <p className="text-sm text-gray-400">
              {filteredReceptions.length} réception(s) trouvée(s)
            </p>
            {error && (
              <p className="text-sm text-red-400">❌ {error}</p>
            )}
          </div>
        </div>

        {/* Liste des réceptions */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && receptions.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <LoaderIcon className="animate-spin h-12 w-12 text-purple-500 mx-auto mb-4" />
                <p className="text-gray-400">Chargement des réceptions...</p>
              </div>
            </div>
          ) : filteredReceptions.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">Aucune réception trouvée</p>
                <p className="text-gray-500 text-sm mt-2">
                  {receptions.length === 0 
                    ? "Créez votre première réception pour commencer"
                    : "Essayez une autre recherche"
                  }
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredReceptions.map((reception) => (
                <motion.div
                  key={reception.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-slate-700/50 rounded-lg border-2 transition-all cursor-pointer ${
                    selectedReception?.id === reception.id
                      ? "border-purple-500 bg-purple-900/20"
                      : "border-slate-600 hover:border-slate-500"
                  }`}>
                  
                  {/* En-tête réception */}
                  <div
                    onClick={() => handleSelect(reception)}
                    className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Car className="w-5 h-5 text-purple-400" />
                          <h3 className="text-lg font-bold text-white">
                            {reception.immatriculation || "N/A"}
                          </h3>
                          {selectedReception?.id === reception.id && (
                            <span className="px-2 py-1 bg-purple-500 text-white text-xs rounded-full flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Sélectionné
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-gray-300">
                            <User className="w-4 h-4 text-gray-400" />
                            {reception.client_name || "Client non renseigné"}
                          </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {reception.date_reception 
                              ? new Date(reception.date_reception).toLocaleDateString("fr-FR")
                              : "Date non renseignée"
                            }
                          </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <Clock className="w-4 h-4 text-gray-400" />
                            {reception.heure_reception || "08:00"}
                          </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <ImageIcon className="w-4 h-4 text-gray-400" />
                            {reception.photos && typeof reception.photos === "object"
                              ? `${Object.keys(reception.photos).length} photo(s)`
                              : "0 photo"
                            }
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(reception.id);
                        }}
                        className="text-gray-400 hover:text-white transition-colors p-2">
                        {expandedReception === reception.id ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Détails étendus */}
                  <AnimatePresence>
                    {expandedReception === reception.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-slate-600 overflow-hidden">
                        <div className="p-4 space-y-4">
                          {/* Notes */}
                          {reception.notes && (
                            <div>
                              <p className="text-sm text-gray-400 mb-1">Notes :</p>
                              <p className="text-gray-300 text-sm bg-slate-800 p-3 rounded">
                                {reception.notes}
                              </p>
                            </div>
                          )}

                          {/* Photos */}
                          {photosUrls[reception.id] &&
                            Object.keys(photosUrls[reception.id]).length > 0 && (
                              <div>
                                <p className="text-sm text-gray-400 mb-2">
                                  Photos ({Object.keys(photosUrls[reception.id]).length}) :
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                  {Object.entries(photosUrls[reception.id]).map(
                                    ([key, url]) => (
                                      <div key={key} className="relative group">
                                        <img
                                          src={url}
                                          alt={key}
                                          className="w-full h-24 object-cover rounded border border-slate-600"
                                        />
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded">
                                          <Eye className="w-6 h-6 text-white" />
                                        </div>
                                        <p className="text-xs text-gray-400 mt-1 text-center">
                                          {key.replace("Before", "").replace("After", "")}
                                        </p>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}

                          {/* Chargement photos */}
                          {expandedReception === reception.id && 
                           reception.photos && 
                           !photosUrls[reception.id] && (
                            <div className="flex items-center justify-center py-4">
                              <LoaderIcon className="animate-spin w-6 h-6 text-purple-400 mr-2" />
                              <span className="text-gray-400 text-sm">Chargement des photos...</span>
                            </div>
                          )}

                          {/* Dates création/modification */}
                          <div className="text-xs text-gray-500 flex items-center justify-between">
                            <span>
                              Créée le{" "}
                              {reception.created_at 
                                ? new Date(reception.created_at).toLocaleString("fr-FR")
                                : "Date inconnue"
                              }
                            </span>
                            {reception.updated_at && reception.updated_at !== reception.created_at && (
                              <span>
                                Modifiée le{" "}
                                {new Date(reception.updated_at).toLocaleString("fr-FR")}
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Footer avec boutons d\'action */}
        <div className="p-6 border-t border-slate-700 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">
            Annuler
          </button>

          <button
            onClick={confirmSelection}
            disabled={!selectedReception}
            className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Utiliser cette réception
            {selectedReception && ` (${selectedReception.immatriculation})`}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ReceptionSearch;